import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboard, DashboardData } from "../../lib/adminApi";
import { useToast } from "../../components/Toast";
import { czShortDate } from "../../lib/dates";

export default function AdminDashboard() {
  const toast = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
      <p className="mt-2 text-slate-600">Přehled nadcházejících rezervací a klíčová čísla.</p>

      {loading ? (
        <div className="mt-8 text-slate-500">Načítám…</div>
      ) : !data ? (
        <div className="mt-8 text-slate-500">Žádná data.</div>
      ) : (
        <>
          <div className="mt-8 grid gap-4 md:grid-cols-4">
            <KpiCard
              label="Čekají na schválení"
              value={String(data.pendingCount)}
              highlight={data.pendingCount > 0}
              linkTo="/admin/reservations"
              linkLabel="Přejít →"
            />
            <KpiCard
              label="Tento týden"
              value={String(data.upcomingThisWeek)}
              hint="Nadcházející potvrzené"
            />
            <KpiCard
              label="Tento měsíc"
              value={String(data.thisMonth.count)}
              hint="Potvrzené + proběhlé"
            />
            <KpiCard
              label="Tržby tento měsíc"
              value={`${data.thisMonth.revenueCzk.toLocaleString("cs-CZ")} Kč`}
              hint={`${data.thisMonth.distanceKm} km najeto`}
            />
          </div>

          <div className="mt-10 card">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Nejbližší rezervace</h2>
              <Link
                to="/admin/reservations"
                className="text-sm font-medium text-obloha-indigo-700 hover:text-obloha-indigo-900"
              >
                Všechny →
              </Link>
            </div>

            {data.nextReservations.length === 0 ? (
              <div className="mt-4 rounded-lg border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">
                Zatím žádné nadcházející rezervace.
              </div>
            ) : (
              <ul className="mt-4 divide-y divide-slate-200">
                {data.nextReservations.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/admin/reservations/${r.id}`}
                      className="flex items-center justify-between py-3 hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-20 text-sm text-slate-500">
                          {czShortDate(new Date(r.date))}
                          <div className="text-xs">{r.startTime}</div>
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{r.snapshotName}</div>
                          <div className="text-xs text-slate-500">
                            {r.snapshotCity} · {r.childrenCount} dětí
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusDot status={r.status} />
                        <span className="tabular-nums text-sm font-medium text-slate-700">
                          {r.totalPriceCzk.toLocaleString("cs-CZ")} Kč
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  highlight,
  linkTo,
  linkLabel,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className={`card ${highlight ? "ring-2 ring-amber-400" : ""}`}>
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className={`mt-2 text-3xl font-bold ${highlight ? "text-amber-600" : "text-obloha-indigo-900"}`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
      {linkTo && (
        <Link to={linkTo} className="mt-3 inline-block text-xs font-medium text-obloha-indigo-700 hover:text-obloha-indigo-900">
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: "PENDING" | "CONFIRMED" | "COMPLETED" | "CANCELLED" }) {
  const color =
    status === "PENDING" ? "bg-amber-500" : status === "CONFIRMED" ? "bg-obloha-indigo-600" : status === "COMPLETED" ? "bg-emerald-500" : "bg-slate-400";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
      <span className={`h-2 w-2 rounded-full ${color}`}></span>
      {status === "PENDING" ? "čeká" : status === "CONFIRMED" ? "potvrzeno" : status === "COMPLETED" ? "proběhlo" : "zrušeno"}
    </span>
  );
}
